// import node module libraries
import { Card, Form, Button, Row, Col, Badge } from "react-bootstrap";

// import custom components
import { FormSelect } from "components/elements/form-select/FormSelect";
import ReactQuillEditor from "components/elements/editor/ReactQuillEditor";
import { Formik, useFormikContext } from "formik";
import { courseBasicInfoSchema } from "components/schemas/schema";
import { useEffect, useRef, useState } from "react";
import {
  createCourseDetails,
  getConfigOrganization,
} from "ServerAPI/serverAPI";
import { useCookies } from "react-cookie";
import { CircularProgress } from "@mui/material";
import { PlusCircle, Trash2 } from "react-feather";
import { useToast } from "context/toast-context/ToastProvider";

const BasicInformation = (props) => {
  const {
    next,
    courseDetails,
    setCourseDetails,
    courseId,
    setCourseId,
    savedCourse,
    editSavedCourse,
  } = props;
  const [nextBtnClicked, setNextBtnClicked] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [processess, setProcessess] = useState([]);
  const [cookies, setCookies] = useCookies();
  const [organisationDataLoading, setOrganisationDataLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const showOutcomesRef = useRef(false);

  const baiscinfoform = {
    courseTitle: "",
    courseLevel: "",
    courseDescription: "",
    courseDepartment: "",
    courseDomain: "",
    organizationId: cookies?.organizationId,
    coverImage: "",
    learningOutcome: [],
  };

  const CoursesLevel = [
    { value: "Beginner", label: "Beginner" },
    { value: "Intermediate", label: "Intermediate" },
    { value: "Advanced", label: "Advanced" },
  ];

  const goNext = async (values) => {
    // Validate empty course outcomes
    if (showOutcomesRef.current && values.learningOutcome.length > 0) {
      const hasEmpty = values.learningOutcome.some(o => !o || o.trim() === "");
      if (hasEmpty) {
        showToast("All Course Outcomes must be filled in. Please complete or remove empty outcomes.", "error");
        return;
      }
    }

    // Properly map CO1, CO2 labels into the strings before saving
    const mappedOutcomes = values.learningOutcome.map((o, i) => `CO${i + 1}: ${o}`);
    const finalValues = { ...values, learningOutcome: mappedOutcomes };

    if (editSavedCourse) {
      setCourseDetails(prev => ({ ...prev, learningOutcome: mappedOutcomes }));
      next();
      return;
    }
    if (!courseId) {
      setIsLoading(true);
      const payload = {
        ...finalValues,
        authorEmail: cookies?.email,
        creationType: "courseDetails",
        authorUserId: cookies?.userId,
      };
      const response = await createCourseDetails(payload);
      if (response.status === 200) {
        setCourseId(response?.data?.courseId);
        setCourseDetails((pre) => {
          return { ...pre, ...finalValues, courseId: response?.data?.courseId };
        });
      }
      setIsLoading(false);
    } else {
      // If courseId exists but we are in Step 1, we still want to update state
      setCourseDetails(prev => ({ ...prev, ...finalValues }));
    }
    next();
  };
  const fetchOrganisationDetails = async () => {
    setOrganisationDataLoading(true);
    try {
      const details = await getConfigOrganization(
        cookies?.email,
        cookies?.organizationId,
        cookies.userId
      );

      if (details?.status === 200) {
        console.log("Organization Details Response:", details.data);

        // ✅ Ensure data exists before mapping
        const allDepartment = Array.isArray(details.data.department)
          ? details.data.department.map((department) => ({
              value: department?.title,
              label: department?.title,
            }))
          : [];
        console.log("details.data.domain", details.data.domain);

        const allProcessess = Array.isArray(details.data.domain)
          ? details.data.domain.map((domain) => ({
              value: domain?.title,
              label: domain?.title,
            }))
          : [];

        setDepartments(allDepartment);
        setProcessess(allProcessess);
      } else {
        console.warn("No department or domain found for this role.");
        setDepartments([]);
        setProcessess([]);
      }
    } catch (error) {
      console.error("Error fetching organization details:", error);
      setDepartments([]);
      setProcessess([]);
    } finally {
      setOrganisationDataLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganisationDetails();
  }, []);

  const handleOnChange = (e) => {
    const { name, value } = e.target;

    setCourseDetails((pre) => {
      return { ...pre, [name]: value };
    });
  };

  return (
    <Formik
      initialValues={baiscinfoform}
      validationSchema={courseBasicInfoSchema}
      onSubmit={goNext}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {({
        handleSubmit,
        handleChange,
        errors,
        ErrorMessage,
        touched,
        values,
        setFieldValue,
        handleBlur,
      }) => {
        useEffect(() => {
          setFieldValue("courseTitle", courseDetails?.courseTitle);
          setFieldValue("courseDescription", courseDetails?.courseDescription);

          setFieldValue("courseLevel", courseDetails?.courseLevel);
          setFieldValue("courseDepartment", courseDetails?.courseDepartment);
          setFieldValue("courseDomain", courseDetails?.courseDomain);
          
          // Strip "CO1: " prefixes on load so user edits raw text
          const outcomes = Array.isArray(courseDetails?.learningOutcome) 
            ? courseDetails.learningOutcome.map(o => typeof o === 'string' ? o.replace(/^CO\d+:\s*/, '') : o)
            : [];
          setFieldValue("learningOutcome", outcomes);
          
          // If outcomes exist, automatically show the section
          if (outcomes.length > 0) {
            setShowOutcomes(true);
          }
        }, [courseDetails]);

        const [showOutcomes, setShowOutcomes] = useState(Array.isArray(values.learningOutcome) && values.learningOutcome.length > 0);

        // Track whether the course was loaded/saved with existing outcomes
        // When true, the checkbox is locked (disabled) until user deletes all outcomes
        const [hadOutcomesOnLoad, setHadOutcomesOnLoad] = useState(false);

        // Keep the ref in sync so goNext can read it
        showOutcomesRef.current = showOutcomes;

        useEffect(() => {
          // Detect edit mode: savedCourse exists and has learning outcomes
          if (savedCourse) {
            const outcomes = Array.isArray(savedCourse?.learningOutcome) ? savedCourse.learningOutcome : [];
            const stripped = outcomes.map(o => typeof o === 'string' ? o.replace(/^CO\d+:\s*/, '') : o).filter(o => o && o.trim() !== '');
            if (stripped.length > 0) {
              setHadOutcomesOnLoad(true);
            }
          }
        }, [savedCourse]);

        // Determine if checkbox should be disabled:
        // Locked when course was loaded with outcomes AND outcomes still exist
        const isCheckboxLocked = hadOutcomesOnLoad && values.learningOutcome.length > 0;

        const addOutcome = () => {
          const updated = [...values.learningOutcome, ""];
          setFieldValue("learningOutcome", updated);
          setCourseDetails(prev => ({ ...prev, learningOutcome: updated }));
        };

        const removeOutcome = (index) => {
          const updated = values.learningOutcome.filter((_, i) => i !== index);
          setFieldValue("learningOutcome", updated);
          setCourseDetails(prev => ({ ...prev, learningOutcome: updated }));
          // If all outcomes are deleted, auto-untick and unlock the checkbox
          if (updated.length === 0) {
            setShowOutcomes(false);
            setHadOutcomesOnLoad(false);
          }
        };

        const handleOutcomeChange = (index, value) => {
          const updated = [...values.learningOutcome];
          updated[index] = value;
          setFieldValue("learningOutcome", updated);
          setCourseDetails(prev => ({ ...prev, learningOutcome: updated }));
        };

        return (
          <>
            {organisationDataLoading ? (
              <>
                <div className="d-flex justify-content-center mt-5">
                  <CircularProgress />
                </div>
              </>
            ) : (
              <>
                {" "}
                <Form className="">
                  <Card className="mb-3 p-8  ">
                    <Card.Header className="border-bottom px-4 py-3">
                      <h4 className="mb-0">Course Details</h4>
                    </Card.Header>

                    <Card.Body>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="courseTitle">
                          Course Title
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Course Title"
                          id="courseTitle"
                          name="courseTitle"
                          value={courseDetails.courseTitle}
                          onChange={(e) => {
                            handleOnChange(e);
                            handleChange(e);
                          }}
                          required
                          onBlur={handleBlur}
                          className="text-dark"
                        />
                        <Form.Text className="text-muted">
                          Write within 60 words
                        </Form.Text>

                        {errors.courseTitle && nextBtnClicked ? (
                          <>
                            <div style={{ color: "red" }}>
                              {errors.courseTitle}
                            </div>
                          </>
                        ) : null}
                      </Form.Group>

                      <div className="d-flex justify-content-between">
                        <Form.Group className="mb-3">
                          <Form.Label>Department</Form.Label>
                          <FormSelect
                            options={departments}
                            id="courseDepartment"
                            name="courseDepartment"
                            placeholder="Select Department"
                            value={courseDetails.courseDepartment}
                            defaultselected={courseDetails?.courseDepartment}
                            onChange={(e) => {
                              handleOnChange(e);
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                          />
                          {errors.courseDepartment && nextBtnClicked ? (
                            <>
                              <div style={{ color: "red" }}>
                                {errors.courseDepartment}
                              </div>
                            </>
                          ) : null}
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Domain</Form.Label>
                          <FormSelect
                            options={processess}
                            id="courseDomain"
                            name="courseDomain"
                            placeholder="Select Domain"
                            value={courseDetails.courseDomain}
                            defaultselected={courseDetails?.courseDomain}
                            onChange={(e) => {
                              handleOnChange(e);
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                          />
                          {/* <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={age}
                        label="Age"
                        onChange={(e) => {
                          handleOnChange(e);
                          handleChange(e);
                        }}
                      >
                        <MenuItem value={10}>Ten</MenuItem>
                        <MenuItem value={20}>Twenty</MenuItem>
                        <MenuItem value={30}>Thirty</MenuItem>
                      </Select> */}
                          {errors.courseDomain && nextBtnClicked ? (
                            <>
                              <div style={{ color: "red" }}>
                                {errors.courseDomain}
                              </div>
                            </>
                          ) : null}
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Courses level</Form.Label>
                          <FormSelect
                            options={CoursesLevel}
                            id="courseLevel"
                            name="courseLevel"
                            placeholder="Select level"
                            value={courseDetails.courseLevel}
                            defaultselected={courseDetails?.courseLevel}
                            onChange={(e) => {
                              handleOnChange(e);
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                          />
                          {errors.courseLevel && nextBtnClicked ? (
                            <>
                              <div style={{ color: "red" }}>
                                {errors.courseLevel}
                              </div>
                            </>
                          ) : null}
                        </Form.Group>
                      </div>

                      <Form.Group className="mb-3">
                        <Form.Label>Course Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={6}
                          placeholder="Write Course Description"
                          id="courseDescription"
                          name="courseDescription"
                          value={courseDetails.courseDescription}
                          onChange={(e) => {
                            handleOnChange(e);
                            handleChange(e);
                          }}
                          required
                          onBlur={handleBlur}
                          className="text-dark"
                        />

                        <Form.Text className="text-muted">
                          Brief summary of your courses.
                        </Form.Text>
                        {errors.courseDescription && nextBtnClicked ? (
                          <>
                            <div style={{ color: "red" }}>
                              {errors.courseDescription}
                            </div>
                          </>
                        ) : null}
                      </Form.Group>

                      {/* Course Outcomes Section */}
                      <Form.Group className="mb-4">
                        <Form.Check
                          type="checkbox"
                          id="add-outcomes-checkbox"
                          label="Add Course Outcomes"
                          checked={showOutcomes}
                          disabled={isCheckboxLocked}
                          onChange={(e) => {
                            setShowOutcomes(e.target.checked);
                            if (e.target.checked && values.learningOutcome.length === 0) {
                              addOutcome();
                            }
                            if (!e.target.checked) {
                              // Clear all outcomes when unchecking
                              setFieldValue("learningOutcome", []);
                              setCourseDetails(prev => ({ ...prev, learningOutcome: [] }));
                            }
                          }}
                          className="mb-3 fw-bold text-primary"
                          style={isCheckboxLocked ? { opacity: 0.7 } : {}}
                        />

                        {showOutcomes && (
                          <div className="ps-3 border-start border-3 border-primary border-opacity-25">
                            {values.learningOutcome.map((outcome, index) => (
                              <Row key={index} className="align-items-center mb-3 g-2">
                                <Col xs="auto">
                                  <Badge bg="light" text="dark" className="border px-2 py-2 fs-6 fw-bold">
                                    CO{index + 1}
                                  </Badge>
                                </Col>
                                <Col>
                                  <Form.Control
                                    type="text"
                                    placeholder={`Describe Course Outcome ${index + 1}...`}
                                    value={outcome}
                                    onChange={(e) => handleOutcomeChange(index, e.target.value)}
                                    className="text-dark"
                                  />
                                </Col>
                                <Col xs="auto">
                                  <div className="d-flex gap-1">
                                    {index === values.learningOutcome.length - 1 && (
                                      <Button 
                                        variant="outline-primary" 
                                        size="sm" 
                                        onClick={addOutcome}
                                        className="p-1"
                                      >
                                        <PlusCircle size={18} />
                                      </Button>
                                    )}
                                    <Button 
                                      variant="outline-danger" 
                                      size="sm" 
                                      onClick={() => removeOutcome(index)}
                                      className="p-1"
                                    >
                                      <Trash2 size={18} />
                                    </Button>
                                  </div>
                                </Col>
                              </Row>
                            ))}
                          </div>
                        )}
                      </Form.Group>

                      {/* <Form.Group className="mb-3">
                    <Form.Label>Learning Outcomes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      placeholder="Learning outcomes..."
                      id="learningOutcome"
                      name="learningOutcome"
                      value={courseDetails.learningOutcome}
                      onChange={(e) => {
                        handleOnChange(e);
                        handleChange(e);
                      }}
                      required
                      onBlur={handleBlur}
                      className="text-dark"
                    />

                    {errors.learningOutcome && nextBtnClicked ? (
                      <>
                        <div style={{ color: "red" }}>
                          {errors.learningOutcome}
                        </div>
                      </>
                    ) : null}
                  </Form.Group> */}

                      <div className="d-flex justify-content-center">
                        {isLoading ? (
                          <CircularProgress />
                        ) : (
                          <Button
                            variant="primary"
                            onClick={() => {
                              handleSubmit();
                              setNextBtnClicked(true);
                            }}
                          >
                            Save & Next
                          </Button>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Form>
              </>
            )}
          </>
        );
      }}
    </Formik>
  );
};
export default BasicInformation;
